<script>
  import "./automator.css";

  import SequentialWorkflowDesigner from "./Internal/SequentialWorkflowDesigner.svelte";
  import { workflow, toolbar, execution, currentStep, definitions } from "./store";
  import { shouldIgnoreKeyEvent } from "../../js/utils/keyboard";

  import StepEditor from "./PropEditor/WorkflowStepEditor.svelte";
  import RootEditor from "./PropEditor/WorkflowRootEditor.svelte";
  import { generateUUID } from "$utils/uuid";
  import { signalStore } from "$utils/signal-store";
  import { appTheme } from "$utils/app-theme";
  import { createStatusBadgesExtension } from "./Workflow/Badges/statusExtension";
  import { createCommentExtension } from "./Workflow/Badges/commentExtension";
  import { createBlockInclusivityExtension } from "./Workflow/Block/extension";
  import { createCodegenErrorExtension } from "./Workflow/Block/codegenErrorExtension";
  import { createWorkflowRefContextMenuExtension } from "./Workflow/contextMenuExtension";
  import { createWorkflowParentContextMenuExtension } from "./Workflow/workflowParentContextMenuExtension";
  import { createDoubleClickExtension } from "./Workflow/doubleClickExtension";
  import { navigateToWorkflow, resolveWorkflowRefTarget } from "./Workflow/navigateToWorkflow";
  import { unavailableStepsCss } from "./Workflow/unavailableSteps";
  import { onMount } from "svelte";
  import {
    filetree,
    codegenError,
    getCodegenErrorStepId,
    stepWarnings,
    getWarningStepIds,
    workflowParents,
  } from "./store";

  // The designer only supports a single `contextMenu` extension, so merge
  // the "Go to Workflow" and "Go to Parent" item providers into one.
  function combineContextMenuExtensions(menuExtensions) {
    return {
      createItemsProvider(customActionController) {
        const itemsProviders = menuExtensions.map((extension) =>
          extension.createItemsProvider(customActionController),
        );

        return {
          getItems(step, sequence) {
            return itemsProviders.flatMap((provider) => provider.getItems(step, sequence));
          },
        };
      },
    };
  }

  const layoutUpdate = signalStore();

  $: isReadonly = workflow.isReadonly;
  $: toolbox = definitions.toolbox;
  $: isToolboxCollapsed = !$toolbar.show_toolbox;
  $: ($toolbar, layoutUpdate.trigger());

  // The toolbox leaves out steps that cannot be added; any already in the
  // workflow are greyed out (see Workflow/unavailableSteps.js).
  const unavailableTypes = definitions.unavailableTypes;
  let unavailableStyle;

  onMount(() => {
    unavailableStyle = document.createElement("style");
    document.head.appendChild(unavailableStyle);

    return () => unavailableStyle.remove();
  });

  $: if (unavailableStyle) unavailableStyle.textContent = unavailableStepsCss($unavailableTypes);

  const steps = {
    isAutoSelectDisabled: false,
    iconUrlProvider: definitions.iconUrlProvider,
    canInsertStep: (step) => !definitions.unavailableReason(step.type),
  };

  const validator = {
    step: (step) => execution.stepValidator(step.id),
    status: (step) => execution.stepStatus(step.id),
    hasComments: (step) => workflow.stepHasComments(step),
  };

  function onDefinitionChanged({ detail }) {
    workflow.internalUpdate(detail);
  }

  function onSelectedStepIdChanged(event) {
    const { detail } = event;
    execution.setCurrentStep(detail.stepId);
    document.querySelector(`.sqd-toolbox-filter`)?.blur();
  }

  function onIsToolboxCollapsedChanged({ detail }) {
    toolbar.update((state) => {
      return { ...state, show_toolbox: !detail.isCollapsed };
    });
  }

  function onStepDoubleClicked(step) {
    navigateToWorkflow(filetree, resolveWorkflowRefTarget(step));
  }

  const uidGenerator = () => generateUUID("w");

  const extensions = [
    {
      badges: [
        createStatusBadgesExtension(),
        createCommentExtension(),
        createBlockInclusivityExtension(),
        createCodegenErrorExtension(getCodegenErrorStepId, getWarningStepIds),
      ],
      contextMenu: combineContextMenuExtensions([
        createWorkflowRefContextMenuExtension(filetree),
        createWorkflowParentContextMenuExtension(filetree, workflowParents),
      ]),
      clickBehaviorWrapperExtension: createDoubleClickExtension(onStepDoubleClicked),
    },
  ];

  const keyboardConfiguration = {
    canHandleKey(_action, event) {
      return !shouldIgnoreKeyEvent(event);
    },
  };
</script>

<SequentialWorkflowDesigner
  theme={$appTheme}
  definition={$workflow}
  on:definitionChanged={onDefinitionChanged}
  undoStackSize={20}
  {steps}
  {validator}
  toolbox={$toolbar.show_toolbox ? $toolbox : false}
  {uidGenerator}
  {extensions}
  selectedStepId={$currentStep}
  stepEditor={StepEditor}
  rootEditor={RootEditor}
  on:selectedStepIdChanged={onSelectedStepIdChanged}
  {isToolboxCollapsed}
  isEditorCollapsed={!$toolbar.show_prop_pane}
  on:isToolboxCollapsedChanged={onIsToolboxCollapsedChanged}
  isReadonly={$isReadonly}
  badgeStore={execution.badges}
  codegenErrorStore={codegenError}
  stepWarningsStore={stepWarnings}
  layoutStore={layoutUpdate}
  keyboard={keyboardConfiguration}
/>
